import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';

type Mode = 'deterministic' | 'non-deterministic';
type Status = {
  mode: Mode;
  revision: number;
  canEdit: boolean;
  scope: string;
  updatedAt: string | null;
  release: { id: string; label: string; projectHash: string; versionId: string } | null;
};
type Version = { id: string; label: string; createdAt: string };
async function request(url: string, options: RequestInit = {}) {
  const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
  if (!response.ok)
    throw new Error(
      response.status === 409
        ? 'Die Auswahl wurde zwischenzeitlich geändert oder die Version ist nicht verfügbar. Bitte aktualisieren.'
        : response.status === 401
          ? 'Bitte erneut anmelden.'
          : response.status === 403
            ? 'Administratorrechte erforderlich.'
            : response.status === 404
              ? 'Die Auswahl ist nur in der Staging-Testumgebung verfügbar.'
              : 'Die Einstellung konnte nicht geladen oder gespeichert werden.',
    );
  return response.json();
}
export const TelephoneFlowPanel = stateful(() => {
  const [status, setStatus] = useState<Status | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [version, setVersion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let alive = true;
    setBusy(true);
    void (async () => {
      try {
        const current = await request('/api/telephone-flow');
        const project = current.canEdit ? await request('/api/navigate/project') : null;
        if (alive) {
          setStatus(current);
          setVersions(project?.versions ?? []);
          setError('');
        }
      } catch (err) {
        if (alive) {
          setStatus(null);
          setError((err as Error).message);
        }
      } finally {
        if (alive) setBusy(false);
      }
    })();
    const focus = () => setRefresh(Date.now());
    window.addEventListener('focus', focus);
    return () => {
      alive = false;
      window.removeEventListener('focus', focus);
    };
  }, [refresh]);
  const save = async (mode: Mode, versionId?: string) => {
    if (!status || busy || !status.canEdit) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const next = await request('/api/telephone-flow', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, revision: status.revision, ...(versionId ? { versionId } : {}) }),
      });
      setStatus(next);
      setVersion('');
      setNotice('Gespeichert. Die Auswahl gilt ab dem nächsten Telefonanruf.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return html` <section class="zw-telephone-panel" aria-label="Telefonischer Gesprächsablauf">
    <style>
      .zw-telephone-panel {
        max-width: 820px;
        padding: 26px;
        border: 1px solid #e0e5ec;
        border-radius: 14px;
        background: white;
        color: #253247;
        box-shadow: 0 3px 16px #22334a08;
      }
      .zw-telephone-panel h3 {
        margin: 0 0 8px;
        font-size: 21px;
        font-weight: 700;
      }
      .zw-telephone-panel p {
        margin: 8px 0 16px;
        line-height: 1.55;
      }
      .zw-telephone-panel .muted {
        color: #637086;
        font-size: 13px;
      }
      .zw-telephone-panel .choice {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin: 18px 0;
      }
      .zw-telephone-panel button,
      .zw-telephone-panel select {
        padding: 11px 16px;
        border-radius: 8px;
        border: 1px solid #cbd3df;
        background: #fff;
        color: #26354e;
        font: inherit;
      }
      .zw-telephone-panel button {
        cursor: pointer;
        font-weight: 600;
      }
      .zw-telephone-panel button[aria-pressed='true'] {
        background: #244767;
        color: white;
        border-color: #244767;
      }
      .zw-telephone-panel button:disabled,
      .zw-telephone-panel select:disabled {
        opacity: 0.55;
        cursor: default;
      }
      .zw-telephone-panel .release {
        padding: 16px;
        border: 1px solid #e1e7ef;
        background: #f6f8fb;
        border-radius: 10px;
        overflow-wrap: anywhere;
      }
      .zw-telephone-panel .prepare {
        margin-top: 24px;
        padding-top: 20px;
        border-top: 1px solid #e5e9ef;
      }
      .zw-telephone-panel label {
        display: block;
        font-weight: 600;
        margin-bottom: 10px;
      }
      .zw-telephone-panel .prepare-row {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }
      .zw-telephone-panel select {
        flex: 1;
        min-width: 200px;
        max-width: 100%;
      }
      .zw-telephone-panel .error {
        color: #a92330;
      }
      .zw-telephone-panel .success {
        color: #176a48;
      }
    </style>
    <h3>Telefonischer Gesprächsablauf</h3>
    <p class="muted">
      Wählen Sie den Ablauf für neue Telefonanrufe. Laufende Gespräche behalten ihre Version bis zum Ende.
    </p>
    ${status
      ? html`
          <div class="muted">
            Aktiv: <strong>${status.mode === 'deterministic' ? 'Deterministic' : 'Non-deterministic'}</strong> ·
            ${status.scope === 'staging' ? 'Staging' : 'Persönliche Testumgebung'}
          </div>
          <div class="choice" role="group" aria-label="Ablauf für neue Telefonanrufe">
            <button
              aria-pressed=${String(status.mode === 'deterministic')}
              ?disabled=${busy || !status.canEdit || status.mode === 'deterministic'}
              @click=${() => save('deterministic')}
            >
              Deterministic
            </button>
            <button
              aria-pressed=${String(status.mode === 'non-deterministic')}
              ?disabled=${busy || !status.canEdit || !status.release || status.mode === 'non-deterministic'}
              @click=${() => save('non-deterministic')}
            >
              Non-deterministic
            </button>
          </div>
          <div class="release">
            ${status.release
              ? html`<strong>Telefonversion: ${status.release.label}</strong>
                  <div class="muted">${status.release.id}</div>`
              : html`Noch keine Navigate-Version für Telefonanrufe ausgewählt.`}
          </div>
          ${status.canEdit
            ? html`<div class="prepare">
                <label for="zw-telephone-version">Getestete Navigate-Version übernehmen</label>
                <div class="prepare-row">
                  <select
                    id="zw-telephone-version"
                    .value=${version}
                    ?disabled=${busy}
                    @change=${(event: Event) => setVersion((event.target as HTMLSelectElement).value)}
                  >
                    <option value="">Version auswählen…</option>
                    ${versions.map(
                      item =>
                        html`<option value=${item.id}>
                          ${item.label} · ${new Date(item.createdAt).toLocaleString('de-DE')}
                        </option>`,
                    )}</select
                  ><button ?disabled=${busy || !version} @click=${() => save(status.mode, version)}>
                    Telefonversion übernehmen
                  </button>
                </div>
                <p class="muted">
                  Speichern Sie zuerst unter Navigate → Versionen eine getestete Version. Spätere Änderungen am Entwurf
                  ändern die Telefonversion nicht. Eine übernommene Version gilt bei aktivem Non-deterministic ab dem
                  nächsten Anruf.
                </p>
              </div>`
            : html`<p class="muted">Die Auswahl kann von einem Administrator geändert werden.</p>`}
          <p class="muted">Die Browser-Tests unter Call und Navigate behalten ihren jeweiligen Ablauf.</p>
        `
      : nothing}
    ${busy ? html`<p role="status" class="muted">Wird geladen…</p>` : nothing}
    ${error ? html`<p role="alert" class="error">${error}</p>` : nothing}
    ${notice ? html`<p role="status" class="success">${notice}</p>` : nothing}
    <button
      ?disabled=${busy}
      @click=${() => {
        setNotice('');
        setRefresh(Date.now());
      }}
    >
      Aktualisieren
    </button>
  </section>`;
});
